import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSnackBar } from '../../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import { FeatureThemeProvider } from '../../../../theme/FeatureThemeProvider';
import {
  buildFormStudioRoute,
  buildWorkspaceRoute,
} from '../../utils/businessFunctionRoutes';
import FormStudioShell from '../components/FormStudioShell';
import { FORM_STUDIO_DEFAULT_SECTION, FORM_STUDIO_COPY } from '../constants/formStudioConfig';
import useFormStudio from '../hooks/useFormStudio';
import { normalizeFormStudioSection } from '../utils/formStudioSections';

function BusinessFunctionFormStudioPageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackBar } = useSnackBar();
  const { accountId, workspaceId, formId, section } = useParams();
  const mode = formId ? 'edit' : 'create';
  const [activeSection, setActiveSection] = useState(
    () => normalizeFormStudioSection(section)
  );
  const [showCreateSuccess, setShowCreateSuccess] = useState(
    () => Boolean(location.state?.formStudioJustCreated)
  );
  const studio = useFormStudio({
    mode,
    workspaceId,
    accountId,
    formId,
  });

  useEffect(() => {
    if (section) {
      setActiveSection(normalizeFormStudioSection(section));
    }
  }, [section]);

  useEffect(() => {
    const handlePopState = () => {
      const parts = window.location.pathname.split('/');
      const lastPart = parts[parts.length - 1];
      setActiveSection(normalizeFormStudioSection(lastPart));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!location.state?.formStudioJustCreated) {
      return;
    }

    setShowCreateSuccess(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state?.formStudioJustCreated, navigate]);

  const handleSectionChange = useCallback(
    (nextSection) => {
      const normalizedSection = normalizeFormStudioSection(nextSection);
      setActiveSection(normalizedSection);

      if (mode === 'edit') {
        const nextUrl = buildFormStudioRoute(
          accountId,
          workspaceId,
          formId,
          normalizedSection
        );
        window.history.replaceState(null, '', nextUrl);
      }
    },
    [accountId, formId, mode, workspaceId]
  );

  const handleLockedSectionAttempt = useCallback(() => {
    showSnackBar(FORM_STUDIO_COPY.sectionLocked, 'info');
  }, [showSnackBar]);

  const handleBack = useCallback(() => {
    if (
      studio.isDirty
      && !window.confirm('You have unsaved changes. Leave this page anyway?')
    ) {
      return;
    }
    navigate(buildWorkspaceRoute(accountId, workspaceId));
  }, [accountId, navigate, studio.isDirty, workspaceId]);

  const handleDelete = useCallback(async () => {
    const result = await studio.deleteForm();
    if (result?.success) {
      showSnackBar(FORM_STUDIO_COPY.deleteSuccess, 'success');
      navigate(buildWorkspaceRoute(accountId, workspaceId));
      return;
    }

    showSnackBar(result?.error || FORM_STUDIO_COPY.deleteError, 'error');
  }, [accountId, navigate, showSnackBar, studio, workspaceId]);

  const handleSave = useCallback(async () => {
    const result = await studio.save();
    if (result?.success) {
      showSnackBar(
        mode === 'create' ? 'Form created successfully' : 'Form updated successfully',
        'success'
      );
      if (result.statusSyncWarning) {
        showSnackBar(result.statusSyncWarning, 'warning');
      }

      if (mode === 'create') {
        const createdFormId = result.createdFormId;
        if (!createdFormId) {
          showSnackBar(FORM_STUDIO_COPY.saveError, 'error');
          return;
        }

        navigate(
          buildFormStudioRoute(
            accountId,
            workspaceId,
            createdFormId,
            activeSection
          ),
          {
            replace: true,
            state: { formStudioJustCreated: true },
          }
        );
        return;
      }

      return;
    }

    if (result?.validation && !result.validation.isValid) {
      const message = result.validation.errorSummary?.join(', ')
        || FORM_STUDIO_COPY.saveError;
      showSnackBar(message, 'error');
      return;
    }

    showSnackBar(studio.submitError || FORM_STUDIO_COPY.saveError, 'error');
  }, [
    accountId,
    activeSection,
    mode,
    navigate,
    showSnackBar,
    studio,
    workspaceId,
  ]);

  const handleDismissCreateSuccess = useCallback(() => {
    setShowCreateSuccess(false);
  }, []);

  const handleCreateSuccessAddFields = useCallback(() => {
    setShowCreateSuccess(false);
    handleSectionChange('fields');
  }, [handleSectionChange]);

  return (
    <FormStudioShell
      mode={mode}
      activeSection={activeSection}
      formName={studio.form?.leads_form_name}
      isLoading={studio.isLoading}
      loadError={studio.loadError}
      isDirty={studio.isDirty}
      isSubmitting={studio.isSubmitting}
      hasFormId={studio.hasFormId}
      studio={studio}
      workspaceId={workspaceId}
      formId={formId}
      showCreateSuccess={showCreateSuccess}
      onDismissCreateSuccess={handleDismissCreateSuccess}
      onCreateSuccessAddFields={handleCreateSuccessAddFields}
      onSectionChange={handleSectionChange}
      onLockedSectionAttempt={handleLockedSectionAttempt}
      onBack={handleBack}
      onSave={handleSave}
      onDelete={mode === 'edit' ? handleDelete : undefined}
      onReload={studio.reload}
    />
  );
}

export default function BusinessFunctionFormStudioPage() {
  return (
    <FeatureThemeProvider sx={{ minHeight: 0 }}>
      <BusinessFunctionFormStudioPageContent />
    </FeatureThemeProvider>
  );
}
